import ExcelJS from "exceljs";
import type { CuadreUnit } from "./cuadre";
import { PLATFORM_META, type SgPlatform } from "./platforms";
import { formatOrigins } from "./origins";
import { HEADER_FILL, styleTag } from "./xlsx";

// Barrido homologado: el mismo libro que hoy se arma a mano sobre el export de
// Wialon («barrido wialon octubre.xlsx»: hojas Units, Conciliación y Comps
// Cuentas, con fórmulas vivas), pero para cualquier plataforma y con los datos
// de SG. Quien lo descarga lo quiere idéntico al de Wialon, así que se respetan
// columnas, textos de fecha («dd.mm.yyyy hh:mm:ss am») y fórmulas.
//
// Lo que cambia frente al de Wialon, porque SG no tiene el dato nativo:
// - Facturable (col. O) = facturación de SG al corte, no la ventana de
//   desactivación de 6 meses que se calibró contra la factura de Gurtam.
// - Deactivation = última modificación en SG de los equipos inactivos: SG no
//   guarda la fecha exacta de baja.
// - Creator = cuenta de SG; Account = razón social; Groups vacío.
// Las columnas R:U (facturación, etiquetas y plataformas) son de SG.

const SECTION_FILL = "FF1F4E79";
export const NOTE_FILL = "FFF2F2F2";
export const INPUT_BLUE = "FF0000FF";
export const FONT = "Arial";

export const MONTHS = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
export const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// Corte mensual: último día del mes anterior al del barrido. El de octubre
// corta el 30 de septiembre, como el ejemplo de Wialon.
export const cutoffFor = (year: number, month: number) => new Date(year, month, 0);
export const barridoMonth = (cutoff: Date) => {
  const next = new Date(cutoff.getFullYear(), cutoff.getMonth() + 1, 1);
  return `${MONTHS[next.getMonth()]} ${next.getFullYear()}`;
};
export const homologadoFileName = (platform: SgPlatform, cutoff: Date) =>
  `barrido ${PLATFORM_META[platform].label.toLowerCase()} ${barridoMonth(cutoff)}.xlsx`;

// Texto de fecha como lo exporta Wialon; las fórmulas de L:N lo parten con MID.
// Sin utcOffsetH usa el huso del navegador; con él, ese huso fijo.
export function wialonDate(ms: number | null, utcOffsetH?: number): string | null {
  if (ms == null) return null;
  const fixed = utcOffsetH != null;
  const d = new Date(fixed ? ms + utcOffsetH * 3600e3 : ms);
  const [Y, M, D, hh, mm, ss] = fixed
    ? [d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds()]
    : [d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds()];
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(D)}.${p(M + 1)}.${Y} ${p(hh % 12 || 12)}:${p(mm)}:${p(ss)} ${hh < 12 ? "am" : "pm"}`;
}

// exceljs guarda la fecha en UTC; corrida al huso local para que Excel muestre el día.
export const excelDay = (d: Date) => new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));

export const parseText = (col: string, r: number) =>
  `IF(${col}${r}="","",DATE(VALUE(MID(${col}${r},7,4)),VALUE(MID(${col}${r},4,2)),VALUE(LEFT(${col}${r},2))))`;

// Filas fijas de Conciliación: las fórmulas de las otras hojas apuntan aquí.
const C = { cutoff: 5, invoiced: 6, minUnits: 7, maxSilent: 8, recent: 9, rows: 13, created: 14, active: 15, billable: 16 };
const REF = (row: number) => `Conciliación!$B$${row}`;

const UNIT_HEADERS = [
  "Name", "Creator", "Account", "Device type", "Deactivation", "UID", "Phone", "Last message time", "Created", "Groups", "Custom fields",
  "Creada (fecha)", "Desactivada (fecha)", "Último mensaje (fecha)", "Facturable (1/0)", "Días sin reportar al corte", "Antigüedad al corte (meses)",
  "Facturación SG", "Etiqueta SG", "Etiqueta de cuenta", "Plataformas",
];
export const BILLING_LABEL = { billable: "Facturable", notBillable: "No facturable", inactive: "Inactivo" } as const;

export function section(ws: ExcelJS.Worksheet, row: number, text: string, cols = 3) {
  for (let c = 1; c <= cols; c++) {
    const cell = ws.getCell(row, c);
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: SECTION_FILL } };
    cell.font = { name: FONT, size: 10, bold: true, color: { argb: "FFFFFFFF" } };
  }
  ws.getCell(row, 1).value = text;
}

export function headerRow(ws: ExcelJS.Worksheet, row: number, labels: string[]) {
  labels.forEach((l, i) => {
    const cell = ws.getCell(row, i + 1);
    cell.value = l;
    cell.font = { name: FONT, size: 10, bold: true };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_FILL } };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  });
}

export const f = (formula: string): ExcelJS.CellFormulaValue => ({ formula, date1904: false });

export interface HomologadoOptions {
  cutoff: Date;
  minUnits?: number; // estadísticas sólo con cuentas de al menos N facturables
  maxSilentDays?: number; // «reportando» = último mensaje a N días o menos del corte
  recentDays?: number; // «alta reciente»
}

export function buildHomologado(platform: SgPlatform, units: CuadreUnit[], opts: HomologadoOptions): ExcelJS.Workbook {
  const { cutoff, minUnits = 10, maxSilentDays = 30, recentDays = 90 } = opts;
  const label = PLATFORM_META[platform].label;
  const wb = new ExcelJS.Workbook();
  // Las fórmulas van sin resultado guardado: Excel las calcula al abrir.
  wb.calcProperties = { fullCalcOnLoad: true };

  const sorted = [...units].sort((a, b) => a.accountName.localeCompare(b.accountName, "es") || a.name.localeCompare(b.name, "es"));
  const last = sorted.length + 1; // última fila de Units
  const rng = (col: string) => `Units!$${col}$2:$${col}$${Math.max(2, last)}`;

  // ── Units ────────────────────────────────────────────────────────────────
  const units_ = wb.addWorksheet("Units", { views: [{ state: "frozen", ySplit: 1 }] });
  units_.addRow(UNIT_HEADERS);
  units_.getRow(1).height = 25.5;
  UNIT_HEADERS.forEach((_, i) => {
    const cell = units_.getCell(1, i + 1);
    cell.font = { bold: true };
    cell.alignment = { vertical: "middle", wrapText: true };
    if (i >= 11) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_FILL } };
  });
  sorted.forEach((u, i) => {
    const r = i + 2;
    const row = units_.addRow([
      u.name || u.imei || `#${u.id}`, // nunca vacía: Filas en Units cuenta esta columna
      u.accountName || null,
      u.accountCompany || u.accountName || null,
      u.deviceType || null,
      u.billing === "inactive" ? wialonDate(u.updatedAt) : null,
      u.imei || null,
      u.phone || null,
      wialonDate(u.lastReport),
      wialonDate(u.createdAt),
      null,
      u.plates ? `placas: ${u.plates}` : null,
      f(parseText("I", r)),
      f(parseText("E", r)),
      f(parseText("H", r)),
      f(`IF(OR(L${r}="",L${r}>${REF(C.cutoff)}),0,IF(R${r}="Facturable",1,0))`),
      f(`IF(N${r}="","",MAX(0,${REF(C.cutoff)}-N${r}))`),
      f(`IF(L${r}="","",(${REF(C.cutoff)}-L${r})/30.4375)`),
      BILLING_LABEL[u.billing],
      u.condition || null,
      u.accountCondition || null,
      formatOrigins(u.platforms) || null,
    ]);
    ["L", "M", "N"].forEach((c) => (row.getCell(c).numFmt = "mm-dd-yy"));
    row.getCell("O").numFmt = "0";
    row.getCell("P").numFmt = "0";
    row.getCell("Q").numFmt = "0.0";
    ["R", "S", "T"].forEach((c) => styleTag(row.getCell(c)));
  });
  [28, 18, 34, 22, 22, 18, 14, 22, 22, 10, 18, 18, 18, 18, 18, 18, 18, 16, 16, 16, 24].forEach((w, i) => (units_.getColumn(i + 1).width = w));
  units_.autoFilter = `A1:U${Math.max(1, last)}`;

  // ── Conciliación ─────────────────────────────────────────────────────────
  const k = wb.addWorksheet("Conciliación", { views: [{ showGridLines: false }] });
  [94, 18, 60].forEach((w, i) => (k.getColumn(i + 1).width = w));
  k.getCell("A1").value = `CONCILIACIÓN DE UNIDADES FACTURABLES — ${label.toUpperCase()} vs. HOJA UNITS`;
  k.getCell("A1").font = { name: FONT, size: 13, bold: true };
  k.getCell("A2").value = `Cuadre entre lo que ${label} nos factura y lo que facturamos a nuestros clientes | Cifras en número de unidades`;

  section(k, 4, "PARÁMETROS");
  const param = (row: number, text: string, value: ExcelJS.CellValue, numFmt: string, note: string) => {
    k.getCell(row, 1).value = text;
    const b = k.getCell(row, 2);
    b.value = value;
    b.numFmt = numFmt;
    b.font = { name: FONT, size: 10, color: { argb: INPUT_BLUE } };
    k.getCell(row, 3).value = note;
  };
  param(C.cutoff, "Fecha de corte (fin del periodo facturado)", excelDay(cutoff), "dd/mm/yyyy", cap(`${MONTHS[cutoff.getMonth()]} ${cutoff.getFullYear()}`));
  param(C.invoiced, `Unidades facturadas por ${label}`, null, "#,##0", "Capturar el dato de la factura del periodo; las verificaciones lo comparan");
  param(C.minUnits, "Mín. unidades facturables por cuenta para estadísticas", minUnits, "#,##0", "Cuentas con menos unidades se listan pero no entran en máx./percentiles/mediana/mín.");
  param(C.maxSilent, "Días máx. sin mensaje para considerar unidad 'reportando'", maxSilentDays, "#,##0", "Medido contra la fecha de corte");
  param(C.recent, "Días para considerar 'alta reciente'", recentDays, "#,##0", "Altas dentro de los últimos N días antes del corte");

  const inv = `$B$${C.invoiced}`;
  const diff = (row: number) => f(`IF(${inv}="","",B${row}-${inv})`);
  section(k, 11, `CONCILIACIÓN CON LO FACTURADO POR ${label.toUpperCase()}`);
  headerRow(k, 12, ["Criterio", "Unidades", `Diferencia vs. ${label}`]);
  const line = (row: number, text: string, formula: string, withDiff = true) => {
    k.getCell(row, 1).value = text;
    k.getCell(row, 2).value = f(formula);
    k.getCell(row, 2).numFmt = "#,##0";
    if (withDiff) {
      k.getCell(row, 3).value = diff(row);
      k.getCell(row, 3).numFmt = "#,##0;(#,##0);-";
    }
  };
  line(C.rows, "Filas en Units (todas las unidades de la plataforma en SG)", `COUNTA(${rng("A")})`);
  line(C.created, "Creadas hasta la fecha de corte", `COUNTIFS(${rng("L")},"<="&$B$${C.cutoff})`);
  line(C.active, "Activas en SG (sin contar inactivas)", `COUNTIFS(${rng("R")},"<>Inactivo")`);
  line(C.billable, "Facturables según SG al corte (cuenta facturable, equipo activo, creada hasta el corte)", `SUM(${rng("O")})`);
  for (let c = 1; c <= 3; c++) k.getCell(C.billable, c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: NOTE_FILL } };
  line(17, `Unidades facturadas por ${label}`, `IF(${inv}="","",${inv})`, false);
  line(18, `Diferencia: facturables SG − ${label}`, `IF(${inv}="","",B${C.billable}-${inv})`, false);
  k.getCell(18, 2).numFmt = "#,##0;(#,##0);-";

  // Comps Cuentas: una fila por Account; sus totales alimentan las verificaciones.
  const accounts = new Map<string, number>();
  for (const u of sorted) {
    const a = u.accountCompany || u.accountName;
    if (!a) continue;
    accounts.set(a, (accounts.get(a) ?? 0) + (u.billing === "billable" ? 1 : 0));
  }
  const accountNames = [...accounts.keys()].sort((a, b) => accounts.get(b)! - accounts.get(a)! || a.localeCompare(b, "es"));
  const first = 7;
  const lastAcc = first + Math.max(1, accountNames.length) - 1;
  const total = lastAcc + 1;

  section(k, 20, "VERIFICACIONES");
  headerRow(k, 21, ["Verificación", "Resultado", "Estado"]);
  const check = (row: number, text: string, formula: string, state: string) => {
    k.getCell(row, 1).value = text;
    k.getCell(row, 2).value = f(formula);
    k.getCell(row, 2).numFmt = "#,##0";
    k.getCell(row, 3).value = state.startsWith("=") ? f(state.slice(1)) : state;
  };
  check(22, "Suma de unidades por cuenta (Comps Cuentas) = filas en Units", `'Comps Cuentas'!B${total}`, `=IF(B22=B${C.rows},"OK","REVISAR: unidades sin cuenta")`);
  check(23, "Suma de facturables por cuenta (Comps Cuentas) = facturables SG", `'Comps Cuentas'!C${total}`, `=IF(B23=B${C.billable},"OK","REVISAR")`);
  check(24, `Facturables SG = facturado por ${label}`, `B${C.billable}`, `=IF(${inv}="","SIN CAPTURA",IF(B24=${inv},"OK","DIFERENCIA"))`);
  check(25, "Filas sin UID (revisar posibles duplicados)", `COUNTBLANK(${rng("F")})`, "Informativo");
  check(26, "Facturables que nunca han enviado mensaje", `COUNTIFS(${rng("O")},1,${rng("N")},"")`, "Informativo");
  check(27, "Facturables con último mensaje hace más de N días (excluye los que nunca reportaron)", `COUNTIFS(${rng("O")},1,${rng("P")},">"&$B$${C.maxSilent})`, "Informativo");
  check(28, "Facturables con etiqueta Baja temporal", `COUNTIFS(${rng("O")},1,${rng("S")},"Baja temporal")`, "Informativo");
  check(29, "Facturables dados de alta también en otra plataforma", `COUNTIFS(${rng("O")},1,${rng("U")},"*·*")`, "Informativo");

  section(k, 31, "NOTAS Y METODOLOGÍA");
  [
    `Fuente: SG (cuentas y equipos) cruzado con las plataformas donde está dado de alta cada IMEI y su último reporte (sg-realtime). Hoja Units: ${sorted.length.toLocaleString("es-MX")} filas, cifras al ${new Date().toLocaleDateString("es-MX")}.`,
    "Mismo formato que el barrido de Wialon. Las columnas L:Q de Units son fórmulas auxiliares que convierten los textos de fecha; R:U son datos de SG.",
    "Unidad facturable = equipo activo, de cuenta Facturable en SG, creada hasta la fecha de corte. No usa la ventana de desactivación de Gurtam.",
    "Created = fecha de activación en SG (si no la tiene, la de alta en SG). Deactivation = última modificación en SG de los equipos inactivos: SG no guarda la fecha exacta de baja.",
    "Creator = cuenta de SG; Account = razón social de facturación (si no hay, la cuenta). Groups no aplica fuera de Wialon.",
    "Comps Cuentas: una fila por cuenta (Account). Las estadísticas sólo incluyen cuentas con al menos el mínimo de unidades facturables definido en B7.",
    "Para cuadrar con lo facturado a clientes: capturar en Comps Cuentas, columna L, las unidades que se facturan a cada cliente; la columna M muestra la diferencia.",
  ].forEach((t, i) => (k.getCell(32 + i, 1).value = t));
  k.eachRow((row) => row.eachCell((c) => (c.font = { name: FONT, size: 10, ...c.font })));

  addCompsCuentas(wb, {
    accountNames,
    rng,
    first,
    ref: { cutoff: REF(C.cutoff), minUnits: REF(C.minUnits), maxSilent: REF(C.maxSilent), recent: REF(C.recent) },
    subtitle: "Cada cuenta (Account) de la hoja Units comparada contra el resto | Unidades, % y meses",
    asOf: f(`"Al corte del "&TEXT(${REF(C.cutoff)},"dd/mm/yyyy")&" | Unidades facturables según SG: "&TEXT(${REF(C.billable)},"#,##0")`),
  });

  return wb;
}

// ── Comps Cuentas ──────────────────────────────────────────────────────────
// Una fila por cuenta (Account) con COUNTIFS sobre Units; igual en el
// homologado y en el nativo, sólo cambian las celdas de parámetros.
export interface CompsOptions {
  accountNames: string[];
  rng: (col: string) => string;
  first: number;
  ref: { cutoff: string; minUnits: string; maxSilent: string; recent: string };
  subtitle: string;
  asOf: ExcelJS.CellFormulaValue;
}

export function addCompsCuentas(wb: ExcelJS.Workbook, { accountNames, rng, first, ref, subtitle, asOf }: CompsOptions) {
  const lastAcc = first + Math.max(1, accountNames.length) - 1;
  const total = lastAcc + 1;
  const s = wb.addWorksheet("Comps Cuentas", { views: [{ state: "frozen", xSplit: 1, ySplit: 6, showGridLines: false }] });
  [56, ...Array(13).fill(16)].forEach((w, i) => (s.getColumn(i + 1).width = w));
  s.getCell("A1").value = "COMPARABLES POR CUENTA — UNIDADES FACTURABLES";
  s.getCell("A1").font = { name: FONT, size: 13, bold: true };
  s.getCell("A2").value = subtitle;
  s.getCell("A3").value = asOf;
  section(s, 5, "UNIDADES FACTURABLES POR CUENTA", 14);
  headerRow(s, 6, [
    "Cuenta", "Unidades totales", "Unidades facturables", "% facturable", "Desactivadas", "Facturables reportando", "% reportando",
    "Altas recientes", "% altas recientes", "Antigüedad prom. (meses)", "% del total facturable", "Facturadas a cliente (captura)",
    "Diferencia (facturable − cliente)", "En estadísticas (1/0)",
  ]);
  s.getRow(6).height = 42;
  const FMT = { n: "#,##0", pct: "0.0%", dash: "#,##0;(#,##0);-", dec: "0.0", one: "0" };
  // shortcut: COUNTIFS trata * ? ~ del nombre como comodines; una cuenta con
  // esos caracteres contaría de más. Ninguna los tiene hoy; si aparece, escapar con ~.
  accountNames.forEach((name, i) => {
    const r = first + i;
    const cells: [ExcelJS.CellValue, string][] = [
      [name, ""],
      [f(`COUNTIFS(${rng("C")},$A${r})`), FMT.n],
      [f(`SUMIFS(${rng("O")},${rng("C")},$A${r})`), FMT.n],
      [f(`IF(B${r}=0,"",C${r}/B${r})`), FMT.pct],
      [f(`COUNTIFS(${rng("C")},$A${r},${rng("M")},">0")`), FMT.dash],
      [f(`COUNTIFS(${rng("C")},$A${r},${rng("O")},1,${rng("P")},"<="&${ref.maxSilent})`), FMT.dash],
      [f(`IF(C${r}=0,"",F${r}/C${r})`), FMT.pct],
      [f(`COUNTIFS(${rng("C")},$A${r},${rng("O")},1,${rng("L")},">"&(${ref.cutoff}-${ref.recent}))`), FMT.dash],
      [f(`IF(C${r}=0,"",H${r}/C${r})`), FMT.pct],
      [f(`IFERROR(AVERAGEIFS(${rng("Q")},${rng("C")},$A${r},${rng("O")},1),"")`), FMT.dec],
      [f(`IF($C$${total}=0,"",C${r}/$C$${total})`), FMT.pct],
      [null, FMT.n],
      [f(`IF(L${r}="","",C${r}-L${r})`), FMT.dash],
      [f(`IF(C${r}>=${ref.minUnits},1,0)`), FMT.one],
    ];
    cells.forEach(([v, fmt], c) => {
      const cell = s.getCell(r, c + 1);
      cell.value = v;
      if (fmt) cell.numFmt = fmt;
    });
  });
  const span = (col: string) => `${col}${first}:${col}${lastAcc}`;
  const totals: [string, string, string][] = [
    ["B", `SUM(${span("B")})`, FMT.n],
    ["C", `SUM(${span("C")})`, FMT.n],
    ["D", `IF(B${total}=0,"",C${total}/B${total})`, FMT.pct],
    ["E", `SUM(${span("E")})`, FMT.dash],
    ["F", `SUM(${span("F")})`, FMT.dash],
    ["G", `IF(C${total}=0,"",F${total}/C${total})`, FMT.pct],
    ["H", `SUM(${span("H")})`, FMT.dash],
    ["I", `IF(C${total}=0,"",H${total}/C${total})`, FMT.pct],
    ["J", `IFERROR(AVERAGEIFS(${rng("Q")},${rng("O")},1),"")`, FMT.dec],
    ["K", `SUM(${span("K")})`, FMT.pct],
    ["L", `IF(COUNT(${span("L")})=0,"",SUM(${span("L")}))`, FMT.n],
    ["M", `IF(COUNT(${span("L")})=0,"",SUM(${span("M")}))`, FMT.dash],
    ["N", `SUM(${span("N")})`, FMT.one],
  ];
  s.getCell(`A${total}`).value = "Total / general";
  for (const [col, formula, fmt] of totals) {
    s.getCell(`${col}${total}`).value = f(formula);
    s.getCell(`${col}${total}`).numFmt = fmt;
  }
  for (let c = 1; c <= 14; c++) {
    const cell = s.getCell(total, c);
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_FILL } };
    cell.font = { name: FONT, size: 10, bold: true };
  }
  // AGGREGATE con división por (N=1): las cuentas fuera de estadísticas dan
  // #¡DIV/0! y la opción 6 las ignora. Igual que en el barrido de Wialon.
  const stats: [string, number, string][] = [
    ["Máximo", 14, "1"],
    ["Percentil 75", 17, "3"],
    ["Mediana", 16, "0.5"],
    ["Percentil 25", 17, "1"],
    ["Mínimo", 15, "1"],
  ];
  stats.forEach(([name, fn, k_], i) => {
    const r = total + 2 + i;
    s.getCell(`A${r}`).value = name;
    s.getCell(`A${r}`).font = { name: FONT, size: 10, bold: true };
    for (const [col, fmt] of [["C", "#,##0.0"], ["D", FMT.pct], ["G", FMT.pct], ["I", FMT.pct], ["J", FMT.dec]] as const) {
      const cell = s.getCell(`${col}${r}`);
      cell.value = f(`IFERROR(_xlfn.AGGREGATE(${fn},6,${col}$${first}:${col}$${lastAcc}/($N$${first}:$N$${lastAcc}=1),${k_}),"")`);
      cell.numFmt = fmt;
    }
    for (let c = 1; c <= 14; c++) s.getCell(r, c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: NOTE_FILL } };
  });
  s.getCell(`A${total + 8}`).value = f(`"Estadísticas sobre "&N${total}&" cuentas con al menos "&${ref.minUnits}&" unidades facturables (columna N = 1)."`);

}
